import { useState, useEffect, useRef } from "react";
import { authHeaders } from "../lib/auth";
import AppHeader from "./AppHeader";

// ── Colors ────────────────────────────────────────────────────────────
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D",
  green:"#1E8A4C", warning:"#F29423", danger:"#D93025", orange:"#E07020",
};

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';
// Phase 2: identity rides via authHeaders() (X-WTR-Auth session token), so the
// server trusts the verified session over the client email. The shared service
// token still authorizes the API call; it now lives only in lib/auth.

// ── Stroke-based SVG icons (only what Profile now uses) ───────────────
function PIcon({ name, size = 16, color = "#6B7A8D" }) {
  const s = size, ic = color;
  const icons = {
    user: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke={ic} strokeWidth="1.5"/><path d="M4 20C4 17 7.58 14 12 14C16.42 14 20 17 20 20" stroke={ic} strokeWidth="1.5" strokeLinecap="round"/></svg>,
    ruler: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><rect x="3" y="5" width="18" height="14" rx="2" stroke={ic} strokeWidth="1.5"/><line x1="7" y1="5" x2="7" y2="10" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/><line x1="11" y1="5" x2="11" y2="8" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/><line x1="15" y1="5" x2="15" y2="10" stroke={ic} strokeWidth="1.2" strokeLinecap="round"/></svg>,
    shield: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M12 2L4 6V11C4 16.5 7.5 21.25 12 22.5C16.5 21.25 20 16.5 20 11V6L12 2Z" stroke={ic} strokeWidth="1.5" strokeLinejoin="round"/><path d="M9 12L11 14L15 10" stroke={ic} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    check: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={ic} strokeWidth="1.5"/><path d="M8 12L11 15L16 9" stroke={ic} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    chevronDown: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M6 9L12 15L18 9" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    chevronUp: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M6 15L12 9L18 15" stroke={ic} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  };
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",verticalAlign:"middle"}}>{icons[name]||null}</span>;
}

// ── Field primitives ──────────────────────────────────────────────────
function Label({children}) {
  return <div style={{fontSize:13,fontWeight:700,color:C.body,marginBottom:7,letterSpacing:0.1}}>{children}</div>;
}

function TextInput({value,onChange,placeholder,type="text",unit,disabled}) {
  const [focused,setFocused]=useState(false);
  return (
    <div style={{position:"relative"}}>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled}
        onFocus={()=>setFocused(true)} onBlur={()=>setFocused(false)}
        style={{
          width:"100%", padding: unit?"12px 44px 12px 14px":"12px 14px",
          fontSize:15, fontWeight:600, color:C.body,
          background: disabled ? C.bg : C.white, border:`1.5px solid ${focused?C.blue:C.border}`,
          borderRadius:12, outline:"none", fontFamily:"Nunito Sans,sans-serif",
          boxShadow: focused?`0 0 0 3px ${C.blue}18`:"none",
          transition:"border-color 0.2s, box-shadow 0.2s", WebkitAppearance:"none",
          opacity: disabled ? 0.7 : 1,
        }}/>
      {unit && <span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:12,fontWeight:700,color:C.muted,pointerEvents:"none"}}>{unit}</span>}
    </div>
  );
}

function Chips({value,onChange,options,disabled}) {
  return (
    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
      {options.map(([v,l])=>(
        <button key={v} onClick={()=>!disabled&&onChange(v)} style={{
          padding:"8px 16px", borderRadius:20,
          border:`1.5px solid ${value===v?C.blue:C.border}`,
          background: value===v?C.blueLight:C.white,
          color: value===v?C.blue:C.muted,
          fontSize:13, fontWeight: value===v?700:500,
          cursor: disabled?"default":"pointer", transition:"all 0.18s",
          boxShadow: value===v?`0 2px 8px ${C.blue}25`:"none",
          opacity: disabled ? 0.7 : 1,
        }}>{l}</button>
      ))}
    </div>
  );
}

function SectionTitle({icon,text,children}) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
      <div style={{width:36,height:36,borderRadius:10,background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <PIcon name={icon} size={18} color={C.blue}/>
      </div>
      <div style={{flex:1,fontSize:16,fontWeight:800,color:C.body,letterSpacing:-0.2}}>{text}</div>
      {children}
    </div>
  );
}

// ── Collapsible Section ───────────────────────────────────────────────
function CollapsibleSection({ icon, text, defaultOpen, children, badge }) {
  const [open, setOpen] = useState(defaultOpen !== false);
  return (
    <div style={{background:C.white,borderRadius:20,border:`1px solid ${C.border}`,overflow:"hidden",transition:"all 0.3s"}}>
      <button onClick={()=>setOpen(v=>!v)} style={{
        width:"100%",display:"flex",alignItems:"center",gap:10,padding:"16px 20px",
        background:"transparent",border:"none",cursor:"pointer",textAlign:"left",
      }}>
        <div style={{width:36,height:36,borderRadius:10,background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <PIcon name={icon} size={18} color={C.blue}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:16,fontWeight:800,color:C.body,letterSpacing:-0.2}}>{text}</div>
          {badge && <div style={{fontSize:11,color:C.muted,marginTop:1}}>{badge}</div>}
        </div>
        <PIcon name={open?"chevronUp":"chevronDown"} size={18} color={C.muted}/>
      </button>
      {open && (
        <div style={{padding:"0 20px 20px",animation:"fadeIn 0.2s ease"}}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Personal Info fields (single source, used in both saved + unsaved cards) ──
function PersonalInfoFields({ profile, setP }) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div><Label>First Name</Label><TextInput value={profile.firstName} onChange={v=>setP("firstName",v)} placeholder="First"/></div>
        <div><Label>Last Name</Label><TextInput value={profile.lastName} onChange={v=>setP("lastName",v)} placeholder="Last"/></div>
      </div>
      <div><Label>Email</Label><TextInput value={profile.email} onChange={v=>setP("email",v)} placeholder="you@email.com" type="email"/></div>
      <div><Label>Street Address</Label><TextInput value={profile.address} onChange={v=>setP("address",v)} placeholder="123 Main St"/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        <div><Label>ZIP Code</Label><TextInput value={profile.zip} onChange={v=>setP("zip",v)} placeholder="92201"/></div>
        <div><Label>Age</Label><TextInput value={profile.age} onChange={v=>setP("age",v)} placeholder="30" type="number" unit="yrs"/></div>
      </div>
      <div>
        <Label>Biological Sex</Label>
        <Chips value={profile.gender} onChange={v=>setP("gender",v)} options={[["male","Male"],["female","Female"],["other","Other"]]}/>
      </div>
    </div>
  );
}

// ── Body Metrics fields (single source, used in both saved + unsaved cards) ──
function BodyMetricsFields({ profile, setP, bmi, bmiLabel, bmiColor }) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
          <span style={{fontSize:13,fontWeight:700,color:C.body}}>Height</span>
          <div style={{display:"flex",gap:4}}>
            {[["ft","ft \u00B7 in"],["cm","cm"]].map(([v,l])=>(
              <button key={v} onClick={()=>setP("heightUnit",v)} data-testid={`height-unit-${v}`} style={{padding:"4px 10px",borderRadius:10,border:`1px solid ${profile.heightUnit===v?C.blue:C.border}`,background:profile.heightUnit===v?C.blueLight:C.white,color:profile.heightUnit===v?C.blue:C.muted,fontSize:11,fontWeight:700,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
        </div>
        <TextInput value={profile.height} onChange={v=>setP("height",v)} placeholder={profile.heightUnit==="ft"?"5.11 (5ft 11in)":"180"} type="number" unit={profile.heightUnit==="ft"?"ft.in":"cm"}/>
      </div>
      <div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:7}}>
          <span style={{fontSize:13,fontWeight:700,color:C.body}}>Weight</span>
          <div style={{display:"flex",gap:4}}>
            {[["lbs","lbs"],["kg","kg"]].map(([v,l])=>(
              <button key={v} onClick={()=>setP("weightUnit",v)} data-testid={`weight-unit-${v}`} style={{padding:"4px 10px",borderRadius:10,border:`1px solid ${profile.weightUnit===v?C.blue:C.border}`,background:profile.weightUnit===v?C.blueLight:C.white,color:profile.weightUnit===v?C.blue:C.muted,fontSize:11,fontWeight:700,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
        </div>
        <TextInput value={profile.weight} onChange={v=>setP("weight",v)} placeholder={profile.weightUnit==="lbs"?"170":"77"} type="number" unit={profile.weightUnit}/>
      </div>
      {bmi&&(
        <div data-testid="bmi-card" style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 14px",background:`${bmiColor}10`,borderRadius:14,border:`1px solid ${bmiColor}25`}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:C.muted,letterSpacing:0.8,textTransform:"uppercase"}}>BMI</div>
            <div style={{fontSize:11,color:C.muted}}>Auto-calculated</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:26,fontWeight:900,color:bmiColor,fontFamily:"Nunito,sans-serif",lineHeight:1}}>{bmi}</div>
            <div style={{fontSize:11,fontWeight:700,color:bmiColor}}>{bmiLabel}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MAIN EXPORT ───────────────────────────────────────────────────────
export default function ProfileScreen({ onClose }) {
  const [profileSaved, setProfileSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [justCreated, setJustCreated] = useState(false);

  // Profile state. Personal Info + Body Metrics are edited here. The remaining
  // fields (activity, exercise, climate, hub ids, pwsid) are loaded for display
  // continuity but are NO LONGER sent on save; the server preserves them via
  // COALESCE while their owning screens (My Health, Devices & Integrations) are
  // built. They are NOT edited or written here.
  const [profile, setProfile] = useState(() => {
    const blank = {
      firstName:"", lastName:"", email:"",
      age:"", gender:"",
      height:"", heightUnit:"ft",
      weight:"", weightUnit:"lbs",
      activity:"moderate", exercise:"moderate", exerciseMins:"45", climate:"mild",
      address:"", zip:"", deviceId:"", pwsid:"", pwsName:"",
    };
    try {
      const saved = localStorage.getItem('wtr_profile');
      if (saved) return { ...blank, ...JSON.parse(saved) };
    } catch(e) {}
    return blank;
  });

  // Intake (source mix) is no longer edited or saved on Profile. It moved to
  // My Intake, which is now the ONLY writer of intake. Loaded here only for any
  // local consumers; it is NOT sent on save, so Profile cannot overwrite a live
  // intake log. This is the change that closes the intake clobber race.
  const [intake, setIntake] = useState(() => {
    try {
      const saved = localStorage.getItem('wtr_intake');
      if (saved) return { hub:"1.5", filtered:"0", tap:"0", coffee:"0.4", sports:"0", other:"0", ...JSON.parse(saved) };
    } catch(e) {}
    return { hub:"1.5", filtered:"0", tap:"0", coffee:"0.4", sports:"0", other:"0" };
  });

  // The Oura PAT is preserved across saves via a ref loaded from the backend.
  // It is deliberately NOT placed in profile state or localStorage so it never
  // gets serialized into wtr_profile, and it is NOT sent on save (the server
  // preserves it via COALESCE). Connecting Oura moves to Devices & Integrations.
  // NAMED SCALE-DEBT (ADR Decision 2): server-side OAuth2 with encrypted
  // per-user tokens is the late-July production answer.
  const ouraPatRef = useRef(null);

  const setP = (k,v) => setProfile(p=>({...p,[k]:v}));

  // Persist Profile + intake to localStorage (offline bridge).
  useEffect(() => { try { localStorage.setItem('wtr_profile', JSON.stringify(profile)); } catch(e) {} }, [profile]);
  useEffect(() => { try { localStorage.setItem('wtr_intake', JSON.stringify(intake)); } catch(e) {} }, [intake]);

  // ── Load saved profile from the backend once we have a valid email ──
  const loadedEmailRef = useRef(null);
  useEffect(() => {
    const email = profile.email?.trim().toLowerCase();
    const valid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) { setLoadingProfile(false); return; }
    if (loadedEmailRef.current === email) return;
    let cancelled = false;
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/wtr/profile?email=${encodeURIComponent(email)}`, {
          headers: authHeaders(),
        });
        if (!res.ok) { setLoadingProfile(false); return; }
        const data = await res.json();
        if (!cancelled && data.ok && data.profile) {
          loadedEmailRef.current = email;
          const p = data.profile;
          setProfile(prev => ({
            ...prev,
            firstName: p.first_name || prev.firstName,
            lastName: p.last_name || prev.lastName,
            email: p.email || prev.email,
            age: p.age ? String(p.age) : prev.age,
            gender: p.gender || prev.gender,
            height: p.height || prev.height,
            heightUnit: p.height_unit || prev.heightUnit,
            weight: p.weight || prev.weight,
            weightUnit: p.weight_unit || prev.weightUnit,
            activity: p.activity || prev.activity,
            exercise: p.exercise || prev.exercise,
            exerciseMins: p.exercise_mins || prev.exerciseMins,
            climate: p.climate || prev.climate,
            address: p.home_address || prev.address,
            zip: p.zip_code || prev.zip,
            deviceId: p.hub_device_id || prev.deviceId,
            pwsid: p.pwsid || prev.pwsid,
            pwsName: p.pws_name || prev.pwsName,
          }));
          if (p.intake && typeof p.intake === 'object') {
            setIntake(prev => ({ ...prev, ...p.intake }));
          }
          if (p.oura_pat) ouraPatRef.current = p.oura_pat; // preserve, do not display
          setProfileSaved(true);
        }
      } catch(err) {
        console.error('[Profile] Fetch error:', err.message);
      }
      if (!cancelled) setLoadingProfile(false);
    };
    const t = setTimeout(fetchProfile, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [profile.email]);

  // ── BMI (derived) ──
  const bmi = (() => {
    if (!profile.height||!profile.weight) return null;
    let hm;
    if (profile.heightUnit==="ft") {
      const ft = parseInt(profile.height)||0;
      const inch = parseInt((profile.height.split(".")||[])[1])||0;
      hm=(ft*12+inch)*0.0254;
    } else { hm=parseFloat(profile.height)/100; }
    const wkg=profile.weightUnit==="lbs"?parseFloat(profile.weight)*0.453592:parseFloat(profile.weight);
    if (!hm||!wkg) return null;
    return Math.round((wkg/(hm*hm))*10)/10;
  })();
  const bmiLabel = bmi?(bmi<18.5?"Underweight":bmi<25?"Healthy":bmi<30?"Overweight":"Obese"):null;
  const bmiColor = bmi?(bmi<18.5?C.warning:bmi<25?C.green:bmi<30?C.warning:C.danger):C.muted;

  // ── Save handler. NHS is no longer computed or sent from the client; the
  //    server (routes/wtrHydration.js) is the single source of truth for NHS.
  //    Profile sends ONLY its own fields; every other field is preserved
  //    server-side via COALESCE so this save can never clobber it. ──
  const handleSaveProfile = async () => {
    if (!profile.firstName?.trim()) { setSaveError('First name is required'); return; }
    if (!profile.lastName?.trim()) { setSaveError('Last name is required'); return; }
    if (!profile.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
      setSaveError('Valid email is required'); return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const res = await fetch(`${API_BASE}/api/wtr/profile`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          // Profile owns ONLY these fields. Everything else (intake, activity,
          // exercise, exercise_mins, climate, hub_device_id, pwsid, pws_name,
          // oura_pat) is intentionally OMITTED so the server preserves the
          // stored value via COALESCE on update. This is what closes the intake
          // clobber race: My Intake is the only writer of intake, and Profile no
          // longer sends it. REQUIRES the COALESCE build of routes/wtrCapture.js
          // to be live first; on the old route, omitting these would wipe them.
          email: profile.email.trim().toLowerCase(),
          first_name: profile.firstName.trim(),
          last_name: profile.lastName.trim(),
          age: profile.age || null,
          gender: profile.gender || null,
          height: profile.height || null,
          height_unit: profile.heightUnit,
          weight: profile.weight || null,
          weight_unit: profile.weightUnit,
          address: profile.address || null,
          zip: profile.zip || null,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        setSaveError(data.error || 'Save failed');
        setSaving(false);
        return;
      }

      setProfileSaved(true);
      if (data.isNew) {
        setJustCreated(true);
        setTimeout(() => setJustCreated(false), 5000);
      }
      setSaving(false);
    } catch (err) {
      console.error('[Profile] Save error:', err.message);
      setSaveError('Network error. Please try again.');
      setSaving(false);
    }
  };

  // ── Collapsed-section summary badges ──
  const personalInfoBadge = profileSaved
    ? `${profile.firstName} ${profile.lastName} \u00B7 ${profile.email}`
    : null;
  const bodyMetricsBadge = profileSaved && profile.height && profile.weight
    ? `${profile.height}${profile.heightUnit} \u00B7 ${profile.weight}${profile.weightUnit}${bmi ? ` \u00B7 BMI ${bmi}` : ''}`
    : null;

  if (loadingProfile) {
    return (
      <div style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:40,height:40,border:`3px solid ${C.border}`,borderTopColor:C.blue,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
          <div style={{fontSize:14,color:C.muted,fontWeight:600}}>Loading profile...</div>
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="profile-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent} input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none} ::-webkit-scrollbar{width:3px} ::-webkit-scrollbar-thumb{background:#E8EEF2;border-radius:2px}`}</style>

      <AppHeader
        variant="modal"
        title={profileSaved ? `Hi, ${profile.firstName}` : "Profile"}
        pill={profileSaved ? { label: "Profile Active", tone: "live" } : null}
        onClose={onClose}
      />

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:24,paddingBottom:40,animation:"fadeIn 0.3s ease"}}>

        {/* Welcome banner for newly created profiles */}
        {justCreated && (
          <div style={{
            background:`linear-gradient(135deg,${C.green}15,${C.green}08)`,
            border:`1.5px solid ${C.green}30`,
            borderRadius:16,padding:"16px 20px",
            display:"flex",alignItems:"center",gap:12,
            animation:"fadeIn 0.3s ease",
          }}>
            <div style={{width:40,height:40,borderRadius:12,background:`${C.green}18`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <PIcon name="shield" size={20} color={C.green}/>
            </div>
            <div>
              <div style={{fontSize:14,fontWeight:800,color:C.green}}>Profile Created</div>
              <div style={{fontSize:12,color:C.body,marginTop:2}}>Confirmation email sent to {profile.email}</div>
            </div>
          </div>
        )}

        {/* PERSONAL INFO */}
        {profileSaved ? (
          <CollapsibleSection icon="user" text="Personal Info" defaultOpen={false} badge={personalInfoBadge}>
            <PersonalInfoFields profile={profile} setP={setP}/>
          </CollapsibleSection>
        ) : (
          <div data-testid="personal-info-section" style={{background:C.white,borderRadius:20,border:`1px solid ${C.border}`,padding:"20px"}}>
            <SectionTitle icon="user" text="Personal Info"/>
            <PersonalInfoFields profile={profile} setP={setP}/>
          </div>
        )}

        {/* BODY METRICS */}
        {profileSaved ? (
          <CollapsibleSection icon="ruler" text="Body Metrics" defaultOpen={false} badge={bodyMetricsBadge}>
            <BodyMetricsFields profile={profile} setP={setP} bmi={bmi} bmiLabel={bmiLabel} bmiColor={bmiColor}/>
          </CollapsibleSection>
        ) : (
          <div data-testid="body-metrics-section" style={{background:C.white,borderRadius:20,border:`1px solid ${C.border}`,padding:"20px"}}>
            <SectionTitle icon="ruler" text="Body Metrics"/>
            <BodyMetricsFields profile={profile} setP={setP} bmi={bmi} bmiLabel={bmiLabel} bmiColor={bmiColor}/>
          </div>
        )}

        {/* Error message */}
        {saveError && (
          <div style={{padding:"12px 16px",background:`${C.danger}10`,border:`1.5px solid ${C.danger}25`,borderRadius:12,fontSize:13,fontWeight:600,color:C.danger,textAlign:"center"}}>
            {saveError}
          </div>
        )}

        {/* Save / Update button */}
        <button onClick={handleSaveProfile} disabled={saving} data-testid="profile-save-btn" style={{
          width:"100%", padding:"17px 0",
          background: saving ? C.bg : `linear-gradient(135deg,${C.blue},${C.blueDark})`,
          color: saving ? C.muted : "#fff", border:"none", borderRadius:16,
          fontSize:16, fontWeight:800, cursor: saving ? "not-allowed" : "pointer",
          boxShadow: saving ? "none" : `0 6px 24px ${C.blue}45`,
          letterSpacing:0.2, transition:"all 0.2s",
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
        }}>
          {saving ? (
            <>
              <div style={{width:18,height:18,border:`2px solid ${C.muted}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
              Saving...
            </>
          ) : profileSaved ? 'Update Profile' : 'Save Profile'}
        </button>

        <div style={{textAlign:"center",fontSize:11,color:C.muted}}>
          {profileSaved
            ? 'Expand a section above to edit your details, then Update Profile to save.'
            : 'Your data is securely stored and powers your personalized hydration intelligence.'}
        </div>

      </div>
    </div>
  );
}
